import React from "react";
import { X, FolderOpen, Edit3, Trash2 } from "lucide-react";
import Can from "../auth/Can";

interface FolderItem {
  id: string;
  name: string;
  description: string;
  total_file_count: number;
  createdAt: string;
  source: string;
  document_store_type?: string;
}

interface SelectionToolbarProps {
  selectedFolders: Set<string>;
  folders: FolderItem[];
  onClearSelection: () => void;
  onOpenFolder: (folderId: string) => void;
  onEditFolder: (folder: FolderItem) => void;
  onDeleteFolder: (folder: FolderItem) => void;
}

const SelectionToolbar: React.FC<SelectionToolbarProps> = ({
  selectedFolders,
  folders,
  onClearSelection,
  onOpenFolder,
  onEditFolder,
  onDeleteFolder,
}) => {
  // Don't render if no folders are selected
  if (selectedFolders.size === 0) {
    return null;
  }

  // Get the selected folder (for single selection)
  const getSelectedFolder = () => {
    const selectedId = Array.from(selectedFolders)[0];
    return folders.find((f) => f.id === selectedId);
  };

  const selectedFolder = getSelectedFolder();

  return (
    <div className="my-4 border border-gray-200 bg-blue-50 rounded-lg px-4 py-2.5 shadow-sm">
      <div className="flex items-center justify-between">
        {/* Left: Selection Info */}
        <div className="flex items-center gap-3">
          <button
            onClick={onClearSelection}
            className="p-1 hover:bg-white rounded-full transition-colors"
            title="Clear selection"
            aria-label="Clear selection"
          >
            <X className="h-4 w-4 text-gray-600" />
          </button>
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-gray-900">
              {selectedFolders.size} selected
            </span>
            {selectedFolders.size === 1 && selectedFolder?.description && (
              <span className="text-xs text-gray-600 line-clamp-1">
                {selectedFolder.description}
              </span>
            )}
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-1.5">
          {/* Show Open and Edit only for single selection */}
          {selectedFolders.size === 1 && selectedFolder && (
            <>
              <button
                onClick={() => onOpenFolder(selectedFolder.id)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-700 bg-white hover:bg-blue-100 rounded-lg transition-colors border border-blue-200"
                title="Open folder"
              >
                <FolderOpen className="h-4 w-4" />
                <span>Open</span>
              </button>

              <Can permission="documents:edit">
              <button
                onClick={() => onEditFolder(selectedFolder)}
                className="p-1.5 hover:bg-white rounded-lg transition-colors"
                title="Rename"
                aria-label="Rename folder"
              >
                <Edit3 className="h-4 w-4 text-gray-700" />
              </button>
              </Can>
            </>
          )}

          {/* Delete button (works for single or multiple selections) */}
          <Can permission="documents:delete">
          <button
            onClick={() => {
              const firstSelected = Array.from(selectedFolders)[0];
              const folder = folders.find((f) => f.id === firstSelected);
              if (folder) onDeleteFolder(folder);
            }}
            className="p-1.5 hover:bg-white rounded-lg transition-colors"
            title="Delete"
            aria-label="Delete folder"
          >
            <Trash2 className="h-4 w-4 text-red-600" />
          </button>
          </Can>
        </div>
      </div>
    </div>
  );
};

export default SelectionToolbar;
